import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { FiUser, FiMail, FiLock, FiCamera, FiSave, FiLink, FiTrash2, FiPlus, FiCalendar, FiDownload } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { updateProfile, updateEmail, updatePassword } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useI18n } from '../../../contexts/I18nContext';

const SettingsPage = () => {
    const { user } = useAuth();
    const { locale, changeLocale, availableLocales } = useI18n();
    const [activeTab, setActiveTab] = useState('general'); // general | notifications | integrations

    // Profile State
    const [displayName, setDisplayName] = useState(user?.displayName || '');
    const [email, setEmail] = useState(user?.email || '');
    const [password, setPassword] = useState('');
    const [photoURL, setPhotoURL] = useState(user?.photoURL || '');
    const [loading, setLoading] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    // Notification State
    const [notifications, setNotifications] = useState({
        enabled: false,
        workUpdates: {
            shared: true,
            assigned: true,
            access: true
        },
        communication: {
            mentions: true,
            comments: true,
            recognition: true,
            statusUpdates: true,
            messages: true
        },
        collaboration: {
            addedAsCollaborator: true,
            detailsChanged: true,
            portfolio: false,
            tasksAdded: true
        },
        summaries: {
            overdue: true
        },
        others: {
            followUp: true
        },
        sound: true
    });

    useEffect(() => {
        if (user) {
            setDisplayName(user.displayName || '');
            setEmail(user.email || '');
            setPhotoURL(user.photoURL || '');

            // Load settings from Firestore
            const loadSettings = async () => {
                try {
                    const userRef = doc(db, 'users', user.uid);
                    const docSnap = await getDoc(userRef);
                    if (docSnap.exists() && docSnap.data().notifications) {
                        setNotifications(prev => ({
                            ...prev,
                            ...docSnap.data().notifications
                        }));
                    }
                } catch (error) {
                    console.error("Error loading settings:", error);
                }
            };
            loadSettings();

            // Load webhooks
            const loadWebhooks = async () => {
                try {
                    const whRef = doc(db, 'users', user.uid);
                    const whSnap = await getDoc(whRef);
                    if (whSnap.exists() && whSnap.data().webhooks) {
                        setWebhooks(whSnap.data().webhooks);
                    }
                } catch (e) { console.error('Error loading webhooks:', e); }
            };
            loadWebhooks();
        }
    }, [user]);

    // Integrations state
    const [webhooks, setWebhooks] = useState([]);
    const [newWebhook, setNewWebhook] = useState({ name: '', url: '', events: ['task.created', 'task.updated', 'task.completed'] });

    // API Keys state
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyName, setNewKeyName] = useState('');

    useEffect(() => {
        if (!user?.uid) return;
        const loadKeys = async () => {
            try {
                const ref = doc(db, 'users', user.uid);
                const snap = await getDoc(ref);
                if (snap.exists() && snap.data().apiKeys) setApiKeys(snap.data().apiKeys);
            } catch {}
        };
        loadKeys();
    }, [user?.uid]);

    const generateApiKey = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let key = 'gp_';
        for (let i = 0; i < 32; i++) key += chars.charAt(Math.floor(Math.random() * chars.length));
        return key;
    };

    const handleCreateApiKey = async () => {
        if (!newKeyName.trim()) return toast.error('Nombre requerido');
        const key = { id: `ak-${Date.now()}`, name: newKeyName.trim(), key: generateApiKey(), createdAt: new Date().toISOString() };
        const updated = [...apiKeys, key];
        try {
            await updateDoc(doc(db, 'users', user.uid), { apiKeys: updated });
            setApiKeys(updated);
            setNewKeyName('');
            toast.success('API Key creada');
        } catch { toast.error('Error al crear key'); }
    };

    const handleDeleteApiKey = async (keyId) => {
        const updated = apiKeys.filter(k => k.id !== keyId);
        try {
            await updateDoc(doc(db, 'users', user.uid), { apiKeys: updated });
            setApiKeys(updated);
            toast.success('API Key eliminada');
        } catch { toast.error('Error'); }
    };

    const handleAddWebhook = async () => {
        if (!newWebhook.name.trim() || !newWebhook.url.trim()) return toast.error('Nombre y URL son requeridos');
        try {
            const wh = { ...newWebhook, id: `wh-${Date.now()}`, createdAt: new Date().toISOString() };
            const updated = [...webhooks, wh];
            await updateDoc(doc(db, 'users', user.uid), { webhooks: updated });
            setWebhooks(updated);
            setNewWebhook({ name: '', url: '', events: ['task.created', 'task.updated', 'task.completed'] });
            toast.success('Webhook agregado');
        } catch { toast.error('Error al guardar webhook'); }
    };

    const handleDeleteWebhook = async (whId) => {
        try {
            const updated = webhooks.filter(w => w.id !== whId);
            await updateDoc(doc(db, 'users', user.uid), { webhooks: updated });
            setWebhooks(updated);
            toast.success('Webhook eliminado');
        } catch { toast.error('Error al eliminar'); }
    };

    const handleExportICS = () => {
        // Generate .ics calendar file from tasks with due dates
        let ics = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//GestorTareas//ES\n';
        // We don't have tasks here directly, but we can generate from user context
        // For now export a basic template
        ics += 'END:VCALENDAR';
        toast('Exportacion ICS disponible desde la vista de lista (boton exportar CSV)');
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingPhoto(true);
        try {
            const storage = getStorage();
            const storageRef = ref(storage, `users/${user.uid}/profile.jpg`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setPhotoURL(url);
            toast.success('Foto subida, recuerda guardar cambios');
        } catch (error) {
            console.error(error);
            toast.error('Error al subir foto');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Always sync profile to both Auth and Firestore
            await updateProfile(user, { displayName, photoURL });
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, {
                displayName,
                photoURL,
                email: user.email,
                updatedAt: new Date()
            }, { merge: true });

            if (email !== user.email) {
                await updateEmail(user, email);
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, { email });
            }

            if (password) {
                await updatePassword(user, password);
            }

            toast.success('Perfil actualizado correctamente');
            setPassword('');
        } catch (error) {
            console.error(error);
            toast.error('Error al actualizar perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationChange = async (section, key, value) => {
        let newSettings;
        if (section === 'root') {
            newSettings = { ...notifications, [key]: value };
        } else {
            newSettings = {
                ...notifications,
                [section]: {
                    ...notifications[section],
                    [key]: value
                }
            };
        }

        setNotifications(newSettings);

        // Auto-save notifications
        try {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, { notifications: newSettings });

            if (key === 'enabled' && value === true) {
                // Request permission immediately if enabled
                if ("Notification" in window) {
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                        toast.error('Permiso de notificaciones denegado en el navegador');
                    } else {
                        toast.success('Notificaciones de escritorio activadas');
                        new Audio('/notification.mp3').play().catch(() => { }); // Test sound if available
                    }
                }
            }
        } catch (error) {
            console.error("Error saving notification settings:", error);
        }
    };

    const NotificationToggle = ({ label, checked, onChange }) => (
        <div className="flex items-start gap-3 py-2">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer"
            />
            <label className="text-sm text-gray-700 cursor-pointer select-none" onClick={() => onChange(!checked)}>{label}</label>
        </div>
    );

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Ajustes</h1>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    onClick={() => setActiveTab('general')}
                    className={`pb-4 px-6 text-sm font-medium transition-colors relative ${activeTab === 'general' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    General
                    {activeTab === 'general' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />}
                </button>
                <button
                    onClick={() => setActiveTab('notifications')}
                    className={`pb-4 px-6 text-sm font-medium transition-colors relative ${activeTab === 'notifications' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Notificaciones
                    {activeTab === 'notifications' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />}
                </button>
                <button
                    onClick={() => setActiveTab('integrations')}
                    className={`pb-4 px-6 text-sm font-medium transition-colors relative ${activeTab === 'integrations' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Integraciones
                    {activeTab === 'integrations' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />}
                </button>
                <button
                    onClick={() => setActiveTab('api')}
                    className={`pb-4 px-6 text-sm font-medium transition-colors relative ${activeTab === 'api' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    API
                    {activeTab === 'api' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />}
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[500px]">
                {activeTab === 'general' && (
                    <>
                        <div className="p-6 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-800">Perfil</h2>
                            <p className="text-sm text-gray-500">Tu información personal y los ajustes de seguridad.</p>
                        </div>
                        <form onSubmit={handleSaveProfile} className="p-6 space-y-6">
                            {/* Avatar */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Avatar</label>
                                <div className="flex items-center gap-6">
                                    <div className="relative group">
                                        {photoURL ? (
                                            <img src={photoURL} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md" />
                                        ) : (
                                            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                                                <FiUser size={40} />
                                            </div>
                                        )}
                                        <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-sm">
                                            <FiCamera size={16} />
                                            <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                                        </label>
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {uploadingPhoto ? 'Subiendo...' : 'Haz clic en el icono de cámara para cambiar tu foto.'}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm p-2.5 border"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm p-2.5 border"
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex justify-end">
                                <button type="submit" disabled={loading} className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors">
                                    {loading ? 'Guardando...' : 'Guardar cambios'}
                                </button>
                            </div>
                        </form>

                        {/* Language Selector */}
                        <div className="p-6 border-t border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-800 mb-1">Idioma / Language</h2>
                            <p className="text-sm text-gray-500 mb-4">Selecciona el idioma de la interfaz.</p>
                            <div className="flex gap-2">
                                {availableLocales.map(loc => (
                                    <button
                                        key={loc.code}
                                        onClick={() => changeLocale(loc.code)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${locale === loc.code ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'}`}
                                    >
                                        {loc.code === 'es' ? '🇪🇸' : '🇺🇸'} {loc.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'notifications' && (
                    <div className="p-6">
                        <div className="mb-8 p-4 bg-purple-50 rounded-lg border border-purple-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                        Notificaciones de escritorio
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Personaliza qué notificaciones quieres recibir en tu escritorio.
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={notifications.enabled}
                                        onChange={(e) => handleNotificationChange('root', 'enabled', e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>
                        </div>

                        <div className={`space-y-8 transition-opacity ${!notifications.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            {/* Work Updates */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
                                        <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                                    </div>
                                    Actualizaciones sobre tu trabajo
                                </h4>
                                <div className="ml-6 space-y-1">
                                    <NotificationToggle
                                        label="Cuando compartan trabajo contigo"
                                        checked={notifications.workUpdates.shared}
                                        onChange={(val) => handleNotificationChange('workUpdates', 'shared', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando te asignan trabajo"
                                        checked={notifications.workUpdates.assigned}
                                        onChange={(val) => handleNotificationChange('workUpdates', 'assigned', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando alguien solicita acceso a tu trabajo"
                                        checked={notifications.workUpdates.access}
                                        onChange={(val) => handleNotificationChange('workUpdates', 'access', val)}
                                    />
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Communication */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
                                        <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                                    </div>
                                    Comunicación
                                </h4>
                                <div className="ml-6 space-y-1">
                                    <NotificationToggle
                                        label="Alguien menciona a @ti"
                                        checked={notifications.communication.mentions}
                                        onChange={(val) => handleNotificationChange('communication', 'mentions', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando se envían comentarios o archivos adjuntos"
                                        checked={notifications.communication.comments}
                                        onChange={(val) => handleNotificationChange('communication', 'comments', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando se envían stickers de reconocimiento"
                                        checked={notifications.communication.recognition}
                                        onChange={(val) => handleNotificationChange('communication', 'recognition', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando se publican actualizaciones de estado"
                                        checked={notifications.communication.statusUpdates}
                                        onChange={(val) => handleNotificationChange('communication', 'statusUpdates', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando se envían mensajes"
                                        checked={notifications.communication.messages}
                                        onChange={(val) => handleNotificationChange('communication', 'messages', val)}
                                    />
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Collaboration */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
                                        <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                                    </div>
                                    Actualizaciones sobre el trabajo en el que colaboras
                                </h4>
                                <div className="ml-6 space-y-1">
                                    <NotificationToggle
                                        label="Cuando te agregan como colaborador"
                                        checked={notifications.collaboration.addedAsCollaborator}
                                        onChange={(val) => handleNotificationChange('collaboration', 'addedAsCollaborator', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando cambian los detalles del trabajo"
                                        checked={notifications.collaboration.detailsChanged}
                                        onChange={(val) => handleNotificationChange('collaboration', 'detailsChanged', val)}
                                    />
                                    <NotificationToggle
                                        label="Cuando se agregan tareas a un proyecto"
                                        checked={notifications.collaboration.tasksAdded}
                                        onChange={(val) => handleNotificationChange('collaboration', 'tasksAdded', val)}
                                    />
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Resúmenes */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
                                        <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                                    </div>
                                    Resúmenes y Plazos
                                </h4>
                                <div className="ml-6 space-y-1">
                                    <NotificationToggle
                                        label="Notificar cuando una tarea está vencida o próxima a vencer"
                                        checked={notifications.summaries.overdue}
                                        onChange={(val) => handleNotificationChange('summaries', 'overdue', val)}
                                    />
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Sound */}
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    Sonidos
                                </h4>
                                <div className="ml-6">
                                    <NotificationToggle
                                        label="Reproducir sonido de notificación"
                                        checked={notifications.sound}
                                        onChange={(val) => handleNotificationChange('root', 'sound', val)}
                                    />
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {activeTab === 'integrations' && (
                    <div>
                        <div className="p-6 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <FiLink className="text-purple-500" /> Integraciones
                            </h2>
                            <p className="text-sm text-gray-500">Conecta webhooks para recibir notificaciones en Slack, Discord u otros servicios.</p>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Webhooks */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-3">Webhooks salientes</h3>
                                <p className="text-xs text-gray-500 mb-4">Cuando ocurran eventos en tus tareas, enviaremos un POST JSON a estas URLs.</p>

                                {/* Existing webhooks */}
                                {webhooks.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        {webhooks.map(wh => (
                                            <div key={wh.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-800">{wh.name}</p>
                                                    <p className="text-xs text-gray-500 truncate">{wh.url}</p>
                                                    <div className="flex gap-1 mt-1">
                                                        {(wh.events || []).map(ev => (
                                                            <span key={ev} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] rounded font-medium">{ev}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <button onClick={() => handleDeleteWebhook(wh.id)} className="p-1.5 text-gray-400 hover:text-red-500 ml-3">
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Add new webhook */}
                                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 block mb-1">Nombre</label>
                                            <input
                                                type="text"
                                                value={newWebhook.name}
                                                onChange={(e) => setNewWebhook(w => ({ ...w, name: e.target.value }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                                                placeholder="Ej: Slack #proyectos"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 block mb-1">URL del Webhook</label>
                                            <input
                                                type="url"
                                                value={newWebhook.url}
                                                onChange={(e) => setNewWebhook(w => ({ ...w, url: e.target.value }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                                                placeholder="https://hooks.slack.com/..."
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-600 block mb-1">Eventos</label>
                                        <div className="flex flex-wrap gap-2">
                                            {['task.created', 'task.updated', 'task.completed', 'task.deleted', 'comment.added'].map(ev => (
                                                <label key={ev} className="flex items-center gap-1.5 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={newWebhook.events.includes(ev)}
                                                        onChange={() => {
                                                            setNewWebhook(w => ({
                                                                ...w,
                                                                events: w.events.includes(ev) ? w.events.filter(e => e !== ev) : [...w.events, ev]
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                                    />
                                                    <span className="text-xs text-gray-700">{ev}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <button onClick={handleAddWebhook} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                                        <FiPlus size={14} /> Agregar Webhook
                                    </button>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Calendar Export */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                    <FiCalendar className="text-blue-500" /> Google Calendar
                                </h3>
                                <p className="text-xs text-gray-500 mb-3">Exporta tus tareas con fecha de vencimiento como archivo ICS para importar en Google Calendar.</p>
                                <p className="text-xs text-gray-400 italic">Usa el boton de exportar CSV en la barra de herramientas de cada lista, o descarga el ICS desde la vista de calendario.</p>
                            </div>

                            <hr className="border-gray-100" />

                            {/* Info about available integrations */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-2">Servicios compatibles</h3>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { name: 'Slack', desc: 'Usa un Incoming Webhook de Slack', color: 'bg-purple-50 border-purple-200' },
                                        { name: 'Discord', desc: 'Usa un Webhook de canal de Discord', color: 'bg-indigo-50 border-indigo-200' },
                                        { name: 'Microsoft Teams', desc: 'Usa un conector Incoming Webhook', color: 'bg-blue-50 border-blue-200' },
                                        { name: 'Zapier', desc: 'Conecta con Webhooks by Zapier', color: 'bg-orange-50 border-orange-200' },
                                        { name: 'Make', desc: 'Usa un modulo Webhook en Make', color: 'bg-violet-50 border-violet-200' },
                                        { name: 'Custom', desc: 'Cualquier endpoint que acepte POST', color: 'bg-gray-50 border-gray-200' },
                                    ].map(svc => (
                                        <div key={svc.name} className={`p-3 rounded-lg border ${svc.color}`}>
                                            <p className="text-sm font-medium text-gray-800">{svc.name}</p>
                                            <p className="text-[10px] text-gray-500 mt-0.5">{svc.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'api' && (
                    <div>
                        <div className="p-6 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-800">API Publica</h2>
                            <p className="text-sm text-gray-500">Genera API Keys para acceder a tus datos desde aplicaciones externas.</p>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* API Keys */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-3">Tus API Keys</h3>

                                {apiKeys.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        {apiKeys.map(ak => (
                                            <div key={ak.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-800">{ak.name}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <code className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded font-mono">{ak.key.slice(0, 12)}...{ak.key.slice(-4)}</code>
                                                        <button
                                                            onClick={() => { navigator.clipboard.writeText(ak.key); toast.success('Copiada'); }}
                                                            className="text-[10px] text-blue-600 hover:underline"
                                                        >
                                                            Copiar
                                                        </button>
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 mt-1">Creada: {new Date(ak.createdAt).toLocaleDateString('es-ES')}</p>
                                                </div>
                                                <button onClick={() => handleDeleteApiKey(ak.id)} className="p-1.5 text-gray-400 hover:text-red-500 ml-3">
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newKeyName}
                                        onChange={(e) => setNewKeyName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreateApiKey(); }}
                                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                                        placeholder="Nombre de la key (ej: Mi App)"
                                    />
                                    <button onClick={handleCreateApiKey} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                                        <FiPlus size={14} /> Generar Key
                                    </button>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* API Documentation */}
                            <div>
                                <h3 className="font-semibold text-gray-800 mb-3">Documentacion de la API</h3>
                                <p className="text-xs text-gray-500 mb-4">Usa tu API Key en el header <code className="bg-gray-100 px-1 rounded">Authorization: Bearer gp_xxx</code></p>

                                <div className="space-y-3">
                                    {[
                                        { method: 'GET', path: '/api/v1/projects', desc: 'Listar proyectos' },
                                        { method: 'GET', path: '/api/v1/projects/:id/tasks', desc: 'Listar tareas de un proyecto' },
                                        { method: 'POST', path: '/api/v1/tasks', desc: 'Crear una tarea' },
                                        { method: 'PUT', path: '/api/v1/tasks/:id', desc: 'Actualizar una tarea' },
                                        { method: 'DELETE', path: '/api/v1/tasks/:id', desc: 'Eliminar una tarea' },
                                        { method: 'GET', path: '/api/v1/goals', desc: 'Listar metas' },
                                        { method: 'POST', path: '/api/v1/webhooks/test', desc: 'Probar webhook' },
                                    ].map((ep, i) => (
                                        <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                                ep.method === 'GET' ? 'bg-green-100 text-green-700' :
                                                ep.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                                                ep.method === 'PUT' ? 'bg-amber-100 text-amber-700' :
                                                'bg-red-100 text-red-700'
                                            }`}>
                                                {ep.method}
                                            </span>
                                            <code className="text-xs text-gray-700 font-mono flex-1">{ep.path}</code>
                                            <span className="text-xs text-gray-500">{ep.desc}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-xs text-amber-800">
                                        <strong>Nota:</strong> La API REST requiere Firebase Cloud Functions desplegadas.
                                        Las API Keys generadas aqui estaran listas para usar cuando se activen los endpoints.
                                        Mientras tanto, puedes usar los webhooks salientes para integraciones.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;
